import { useEffect, useRef } from 'react';
import { Platform } from 'react-native';
import { useRouter } from 'expo-router';
import { useAuthStore } from '@/store';
import { isNativePushPlatform, registerDevicePushToken } from '@/lib/pushNotifications';

function hrefFromNotification(notification) {
  const href = notification?.request?.content?.data?.href;
  return typeof href === 'string' && href.startsWith('/') ? href : null;
}

export default function usePushNotifications() {
  const router = useRouter();
  const token = useAuthStore((state) => state.token);
  const responseSub = useRef(null);
  const lastHandledId = useRef(null);

  useEffect(() => {
    if (!isNativePushPlatform() || !token) return undefined;
    let cancelled = false;

    (async () => {
      try {
        await registerDevicePushToken();
      } catch (error) {
        if (!cancelled) {
          console.warn('[push] register failed', error?.message || error);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    if (!isNativePushPlatform()) return undefined;

    let isMounted = true;
    const openFromResponse = (response) => {
      const id = response?.notification?.request?.identifier;
      if (id && lastHandledId.current === id) return;
      if (id) lastHandledId.current = id;
      const href = hrefFromNotification(response?.notification);
      if (href) router.push(href);
    };

    (async () => {
      const Notifications = await import('expo-notifications');
      Notifications.setNotificationHandler({
        handleNotification: async () => ({
          shouldShowAlert: false,
          shouldPlaySound: false,
          shouldSetBadge: false,
          shouldShowBanner: false,
          shouldShowList: false,
        }),
      });

      const last = await Notifications.getLastNotificationResponseAsync();
      if (isMounted) openFromResponse(last);

      responseSub.current = Notifications.addNotificationResponseReceivedListener((response) => {
        openFromResponse(response);
      });
    })().catch((error) => {
      console.warn('[push] listener setup failed', error?.message || error);
    });

    return () => {
      isMounted = false;
      responseSub.current?.remove?.();
      responseSub.current = null;
    };
  }, [router]);
}
